import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ClipboardList, ChevronRight, MapPin, DollarSign, CalendarDays } from "lucide-react";

const statusColor = {
  scheduled: "bg-blue-100 text-blue-700",
  in_progress: "bg-amber-100 text-amber-700",
  completed: "bg-emerald-100 text-emerald-700",
};
const fmtDate = (d) =>
  new Date(d + "T00:00:00").toLocaleDateString("en-US", {
    weekday: "long",
    month: "short",
    day: "numeric",
  });

export default function CleanerJobs() {
  const { user } = useAuth();
  const [jobs, setJobs] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/jobs").then((r) => setJobs(r.data));
  }, []);

  const myPay = (j) => {
    const mine = (j.cleaners || []).find((c) => c.id === user?.id);
    return mine ? Number(mine.amount) : j.cost_per_cleaner;
  };

  const today = new Date().toISOString().slice(0, 10);
  const upcoming = jobs.filter((j) => j.status !== "completed").sort((a, b) => a.date.localeCompare(b.date));
  const done = jobs.filter((j) => j.status === "completed");

  const JobCard = ({ j }) => (
    <Card
      className="overflow-hidden cursor-pointer active:scale-[0.99] transition-transform border-l-4"
      style={{ borderLeftColor: j.property_color || "#4A7C59" }}
      onClick={() => navigate(`/cleaner/jobs/${j.id}`)}
      data-testid={`cleaner-job-${j.id}`}
    >
      <div className="flex">
        <div
          className="w-24 bg-cover bg-center bg-muted shrink-0"
          style={{
            backgroundImage: `url('${
              j.property_image ||
              "https://images.pexels.com/photos/3965509/pexels-photo-3965509.jpeg"
            }')`,
          }}
        />
        <div className="flex-1 p-4 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <h3 className="font-display font-semibold truncate">{j.property_name}</h3>
            <Badge className={`${statusColor[j.status]} border-0 capitalize shrink-0`}>
              {j.status.replace("_", " ")}
            </Badge>
          </div>
          <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
            <CalendarDays className="h-3.5 w-3.5" /> {fmtDate(j.date)}
            {j.date === today && <span className="text-primary font-medium ml-1">· Today</span>}
          </p>
          <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1">
              <DollarSign className="h-3.5 w-3.5" />${Number(myPay(j)).toFixed(2)} payout
            </span>
            <ChevronRight className="h-4 w-4 ml-auto" />
          </div>
        </div>
      </div>
    </Card>
  );

  return (
    <div className="p-4 pb-24">
      <h1 className="font-display text-2xl font-semibold tracking-tight mb-1 mt-2">My Jobs</h1>
      <p className="text-sm text-muted-foreground mb-6">
        Tap a job to view access details and upload photos.
      </p>

      {jobs.length === 0 && (
        <Card className="p-10 text-center">
          <ClipboardList className="h-9 w-9 text-muted-foreground mx-auto mb-3" strokeWidth={1.3} />
          <p className="text-muted-foreground text-sm">
            No jobs assigned to you yet. Check back soon.
          </p>
        </Card>
      )}

      {upcoming.length > 0 && (
        <>
          <p className="text-xs tracking-[0.12em] uppercase font-bold text-muted-foreground mb-2">
            Upcoming
          </p>
          <div className="space-y-3 mb-8">
            {upcoming.map((j) => (
              <JobCard key={j.id} j={j} />
            ))}
          </div>
        </>
      )}

      {done.length > 0 && (
        <>
          <p className="text-xs tracking-[0.12em] uppercase font-bold text-muted-foreground mb-2">
            Completed
          </p>
          <div className="space-y-3 opacity-75">
            {done.map((j) => (
              <JobCard key={j.id} j={j} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
